import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { hrEmployerApi } from "@/lib/api/hr";
import { useHrInvalidate } from "@/hooks/use-hr-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Page, Panel, StatusBadge } from "@/components/shell/page";

export default function EmployerAwardsPage() {
  const invalidate = useHrInvalidate();
  const awards = useQuery({ queryKey: ["hr", "employer", "awards"], queryFn: () => hrEmployerApi.awards() });
  const types = useQuery({ queryKey: ["hr", "employer", "award-types"], queryFn: () => hrEmployerApi.awardTypes() });
  const [employeeId, setEmployeeId] = useState("");
  const [name, setName] = useState("");
  const [points, setPoints] = useState("20");
  const [description, setDescription] = useState("");

  return (
    <Page title="Awards" description="Giving an award also writes a points transaction and a notice.">
      <div className="grid gap-4 lg:grid-cols-5">
        <Panel title="Give an award" className="lg:col-span-2">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="award-employee">Employee ID</Label>
              <Input id="award-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="award-name">Award name</Label>
              <Input
                id="award-name"
                placeholder={types.data?.[0]?.name || "Award name"}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="award-points">Points</Label>
              <Input id="award-points" value={points} onChange={(e) => setPoints(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="award-why">Why</Label>
              <Textarea id="award-why" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <Button
              onClick={async () => {
                await hrEmployerApi.giveAward({
                  employee_id: Number(employeeId),
                  name: name || types.data?.[0]?.name,
                  points: Number(points),
                  description,
                });
                invalidate();
              }}
            >
              Give award
            </Button>
          </div>
        </Panel>
        <div className="space-y-3 lg:col-span-3">
          {(awards.data ?? []).length === 0 && !awards.isLoading && (
            <EmptyState title="No awards yet" body="Awards you give stay on the record, even if you later revoke one." />
          )}
          {(awards.data ?? []).map((award: any) => (
            <div key={award.id} className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">{award.name}</p>
                <p className="text-sm text-muted-foreground">
                  Employee {award.employee_id} · {award.points} pts
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={award.status} />
                {award.status === "active" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await hrEmployerApi.revokeAward(award.id);
                      invalidate();
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
